import { useCallback, useState } from 'react';
import type { ContactFormValue } from '../components/sites/ContactFormFields';
import type {
  WorkerStatsEntry,
  AccidentStatsEntry,
} from '../types/site-stats';

export interface PendingProcessType {
  name: string;
  parentName?: string;
}

export interface SiteCreationConstruction {
  constructionCost: string;
  constructionStart: string;
  constructionEnd: string;
  managementNumber: string;
  licenseNumber: string;
  progressRate: string;
  clientName: string;
  clientContact: string;
}

export const EMPTY_CONSTRUCTION: SiteCreationConstruction = {
  constructionCost: '',
  constructionStart: '',
  constructionEnd: '',
  managementNumber: '',
  licenseNumber: '',
  progressRate: '',
  clientName: '',
  clientContact: '',
};

export interface WorkerStatsByYear {
  current: WorkerStatsEntry | null;
  previous: WorkerStatsEntry | null;
}

export interface AccidentStatsByYear {
  current: AccidentStatsEntry | null;
  previous: AccidentStatsEntry | null;
}

export const EMPTY_WORKER_STATS: WorkerStatsByYear = {
  current: null,
  previous: null,
};

export const EMPTY_ACCIDENT_STATS: AccidentStatsByYear = {
  current: null,
  previous: null,
};

export interface UseSiteCreationStateResult {
  construction: SiteCreationConstruction;
  setConstruction: (next: SiteCreationConstruction) => void;
  clearConstruction: () => void;

  workerStats: WorkerStatsByYear;
  setWorkerStats: (next: WorkerStatsByYear) => void;
  setWorkerStatsCurrent: (next: WorkerStatsEntry | null) => void;
  setWorkerStatsPrevious: (next: WorkerStatsEntry | null) => void;

  accidentStats: AccidentStatsByYear;
  setAccidentStats: (next: AccidentStatsByYear) => void;
  setAccidentStatsCurrent: (next: AccidentStatsEntry | null) => void;
  setAccidentStatsPrevious: (next: AccidentStatsEntry | null) => void;

  processTypes: PendingProcessType[];
  addProcessType: (name: string, parentName?: string) => void;
  removeProcessType: (index: number) => void;
  moveProcessType: (index: number, direction: 'up' | 'down') => void;
  clearProcessTypes: () => void;

  products: string;
  setProducts: (next: string) => void;

  contacts: ContactFormValue[];
  addContact: (contact: ContactFormValue) => void;
  removeContact: (index: number) => void;
  clearContacts: () => void;

  isDirty: boolean;
  reset: () => void;
}

export function useSiteCreationState(): UseSiteCreationStateResult {
  const [construction, setConstruction] =
    useState<SiteCreationConstruction>(EMPTY_CONSTRUCTION);
  const [workerStats, setWorkerStats] =
    useState<WorkerStatsByYear>(EMPTY_WORKER_STATS);
  const [accidentStats, setAccidentStats] =
    useState<AccidentStatsByYear>(EMPTY_ACCIDENT_STATS);
  const [processTypes, setProcessTypes] = useState<PendingProcessType[]>([]);
  const [products, setProducts] = useState('');
  const [contacts, setContacts] = useState<ContactFormValue[]>([]);

  const clearConstruction = useCallback(
    () => setConstruction(EMPTY_CONSTRUCTION),
    [],
  );

  const setWorkerStatsCurrent = useCallback((next: WorkerStatsEntry | null) => {
    setWorkerStats((prev) => ({ ...prev, current: next }));
  }, []);

  const setWorkerStatsPrevious = useCallback(
    (next: WorkerStatsEntry | null) => {
      setWorkerStats((prev) => ({ ...prev, previous: next }));
    },
    [],
  );

  const setAccidentStatsCurrent = useCallback(
    (next: AccidentStatsEntry | null) => {
      setAccidentStats((prev) => ({ ...prev, current: next }));
    },
    [],
  );

  const setAccidentStatsPrevious = useCallback(
    (next: AccidentStatsEntry | null) => {
      setAccidentStats((prev) => ({ ...prev, previous: next }));
    },
    [],
  );

  const addProcessType = useCallback((name: string, parentName?: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setProcessTypes((prev) => [
      ...prev,
      { name: trimmed, parentName: parentName?.trim() || undefined },
    ]);
  }, []);

  const removeProcessType = useCallback((index: number) => {
    setProcessTypes((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const moveProcessType = useCallback(
    (index: number, direction: 'up' | 'down') => {
      setProcessTypes((prev) => {
        const target = direction === 'up' ? index - 1 : index + 1;
        if (target < 0 || target >= prev.length) return prev;
        const next = [...prev];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
    },
    [],
  );

  const clearProcessTypes = useCallback(() => setProcessTypes([]), []);

  const addContact = useCallback((contact: ContactFormValue) => {
    setContacts((prev) => [...prev, contact]);
  }, []);

  const removeContact = useCallback((index: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearContacts = useCallback(() => setContacts([]), []);

  const reset = useCallback(() => {
    setConstruction(EMPTY_CONSTRUCTION);
    setWorkerStats(EMPTY_WORKER_STATS);
    setAccidentStats(EMPTY_ACCIDENT_STATS);
    setProcessTypes([]);
    setProducts('');
    setContacts([]);
  }, []);

  const hasWorkerStats =
    workerStats.current !== null || workerStats.previous !== null;
  const hasAccidentStats =
    accidentStats.current !== null || accidentStats.previous !== null;

  const isDirty =
    construction !== EMPTY_CONSTRUCTION ||
    hasWorkerStats ||
    hasAccidentStats ||
    processTypes.length > 0 ||
    products.trim().length > 0 ||
    contacts.length > 0;

  return {
    construction,
    setConstruction,
    clearConstruction,
    workerStats,
    setWorkerStats,
    setWorkerStatsCurrent,
    setWorkerStatsPrevious,
    accidentStats,
    setAccidentStats,
    setAccidentStatsCurrent,
    setAccidentStatsPrevious,
    processTypes,
    addProcessType,
    removeProcessType,
    moveProcessType,
    clearProcessTypes,
    products,
    setProducts,
    contacts,
    addContact,
    removeContact,
    clearContacts,
    isDirty,
    reset,
  };
}
